import { useState } from 'react';
import { CheckCircle2, Clock, Pencil, Trash2, Check, X } from 'lucide-react';

const CATEGORIES = ['P', 'T', 'C', 'R', 'W', 'H', 'L', 'S', 'SS', 'B', 'A', 'E'];

const InlineEdit = ({ children, editing, onSave, onCancel, input, testId }) => (
    editing ? (
        <span className="inline-flex items-center gap-1">
            {input}
            <button type="button" onClick={onSave} className="p-1 rounded text-green-600 hover:bg-green-50" title="Guardar" data-testid={`${testId}-save`}><Check className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={onCancel} className="p-1 rounded text-slate-400 hover:bg-slate-100" title="Cancelar" data-testid={`${testId}-cancel`}><X className="w-3.5 h-3.5" /></button>
        </span>
    ) : children
);

export const ExtraInvoiceRow = ({ inv, money, onAmountUpdate, onCategoryUpdate, onDelete, onReassign }) => {
    const [editing, setEditing] = useState(null);
    const [value, setValue] = useState('');
    const start = (field) => { setEditing(field); setValue(field === 'amount' ? String(inv.amount) : (inv.category || 'A')); };
    const save = async () => {
        if (editing === 'amount') {
            const n = parseFloat(value);
            if (!n || n <= 0) return;
            if (n !== inv.amount) await onAmountUpdate(inv.id, n);
        } else if (editing === 'category' && value && value !== inv.category) {
            await onCategoryUpdate(inv.id, value);
        }
        setEditing(null);
    };
    const keys = (e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(null); };
    const accDate = (inv.confirmed_at || inv.created_at || '').slice(0, 10);

    return (
        <tr className="border-b border-slate-100 group" data-testid={`extra-invoice-${inv.id}`}>
            <td className="py-2 px-2 text-slate-500 whitespace-nowrap">
                {accDate}
                {inv.deposit_at && inv.deposit_at.slice(0, 10) !== accDate && (
                    <span className="block text-[10px] text-slate-400" title="Día en que llegó el depósito" data-testid={`extra-deposit-date-${inv.id}`}>dep. {inv.deposit_at.slice(0, 10)}</span>
                )}
            </td>
            <td className="py-2 px-2 font-medium">{inv.name}</td>
            <td className="py-2 px-2">
                <button type="button" onClick={() => onReassign?.(inv)} className="inline-flex items-center gap-1 hover:text-fuchsia-700" title="Cambiar usuario de la orden" data-testid={`extra-reassign-btn-${inv.id}`}>
                    {inv.user_name}
                    <Pencil className="w-3 h-3 text-slate-300 group-hover:text-fuchsia-500" />
                </button>
            </td>
            <td className="py-2 px-2 text-slate-600">
                <InlineEdit
                    editing={editing === 'category'}
                    onSave={save}
                    onCancel={() => setEditing(null)}
                    testId={`extra-edit-category-${inv.id}`}
                    input={(
                        <select value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={keys} autoFocus
                            className="h-7 rounded border border-fuchsia-300 bg-white px-1 text-xs font-bold" data-testid={`extra-edit-category-input-${inv.id}`}>
                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    )}
                >
                    <button type="button" onClick={() => start('category')} className="inline-flex items-center gap-1 hover:text-fuchsia-700" title="Editar categoría" data-testid={`extra-edit-category-btn-${inv.id}`}>
                        <span className="font-semibold">{inv.category || '—'}</span>
                        <Pencil className="w-3 h-3 text-slate-300 group-hover:text-fuchsia-500" />
                    </button>
                </InlineEdit>
            </td>
            <td className="py-2 px-2 text-right font-semibold whitespace-nowrap">
                <InlineEdit
                    editing={editing === 'amount'}
                    onSave={save}
                    onCancel={() => setEditing(null)}
                    testId={`extra-edit-amount-${inv.id}`}
                    input={(
                        <input type="number" step="0.01" min="0.01" value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={keys} autoFocus
                            className="w-24 h-7 px-1.5 text-xs text-right border border-fuchsia-300 rounded focus:outline-none focus:ring-1 focus:ring-fuchsia-500" data-testid={`extra-edit-amount-input-${inv.id}`} />
                    )}
                >
                    <button type="button" onClick={() => start('amount')} className="inline-flex items-center gap-1 hover:text-fuchsia-700" title="Editar monto" data-testid={`extra-edit-amount-btn-${inv.id}`}>
                        {money(inv.amount)}
                        <Pencil className="w-3 h-3 text-slate-300 group-hover:text-fuchsia-500" />
                    </button>
                </InlineEdit>
            </td>
            <td className="py-2 px-2">
                {inv.confirmed ? (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full"><CheckCircle2 className="w-3 h-3" /> Confirmada</span>
                ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" /> Pendiente</span>
                )}
            </td>
            <td className="py-2 px-2 text-right">
                <button type="button" onClick={() => onDelete(inv)} className="p-1.5 rounded text-red-500 hover:bg-red-50" title="Eliminar factura" data-testid={`extra-delete-btn-${inv.id}`}>
                    <Trash2 className="w-4 h-4" />
                </button>
            </td>
        </tr>
    );
};

export default ExtraInvoiceRow;
